import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { supabase } from '@/lib/supabase'
import {
  ActiveContext,
  createPersonalContext,
  createOrganizationContext,
} from '@/types/active-context'
import {
  getActiveContext,
  setActiveContext,
  clearActiveContext,
} from '@/lib/active-context-storage'
import { pickActiveContext } from '@/lib/active-context-selection'
import { isAuthDebugEnabled } from '@/lib/debug'

// Debug logging toggle - set to false to reduce console noise
const DEBUG = false
const log = DEBUG ? console.log : () => {}

// Session storage key for cached contexts
const CONTEXTS_CACHE_KEY = 'lina_active_contexts_cache'

// Cache utilities
interface ContextsCache {
  accountId: string
  contexts: ActiveContext[]
  timestamp: number
}

const saveContextsToCache = (accountId: string, contexts: ActiveContext[]) => {
  try {
    const cache: ContextsCache = {
      accountId,
      contexts,
      timestamp: Date.now(),
    }
    sessionStorage.setItem(CONTEXTS_CACHE_KEY, JSON.stringify(cache))
    log('[ActiveContext] Saved contexts to cache')
  } catch (error) {
    console.error('[ActiveContext] Error saving to cache:', error)
  }
}

const loadContextsFromCache = (accountId: string): ActiveContext[] | null => {
  try {
    const cached = sessionStorage.getItem(CONTEXTS_CACHE_KEY)
    if (!cached) {
      log('[ActiveContext] No cached contexts found')
      return null
    }

    const cache: ContextsCache = JSON.parse(cached)

    // Validate cache is for the same account
    if (cache.accountId !== accountId) {
      log('[ActiveContext] Cache is for different account, ignoring')
      sessionStorage.removeItem(CONTEXTS_CACHE_KEY)
      return null
    }

    // Cache is valid for 5 minutes
    const age = Date.now() - cache.timestamp
    if (age > 5 * 60 * 1000) {
      log('[ActiveContext] Cache expired, ignoring')
      sessionStorage.removeItem(CONTEXTS_CACHE_KEY)
      return null
    }

    log('[ActiveContext] Loaded contexts from cache')
    return cache.contexts
  } catch (error) {
    console.error('[ActiveContext] Error loading from cache:', error)
    return null
  }
}

export const clearContextsCache = () => {
  sessionStorage.removeItem(CONTEXTS_CACHE_KEY)
  log('[ActiveContext] Cleared contexts cache')
}

interface ActiveContextState {
  activeContext: ActiveContext | null
  availableContexts: ActiveContext[]
  isLoading: boolean
  switchContext: (context: ActiveContext) => void
  refreshContexts: () => Promise<void>
}

const ActiveContextContext = createContext<ActiveContextState | undefined>(undefined)

export const useActiveContext = () => {
  const context = useContext(ActiveContextContext)
  if (!context) {
    throw new Error('useActiveContext must be used within ActiveContextProvider')
  }
  return context
}

interface ActiveContextProviderProps {
  children: ReactNode
}

interface OrgMembershipRow {
  role: 'owner' | 'admin' | 'member'
  partner_organizations: {
    id: string
    organization_name: string
    plan_id: string | null
    partnership_stage: string
  } | null
}

export const ActiveContextProvider = ({ children }: ActiveContextProviderProps) => {
  const { accountId, accountData, isLoading: authLoading, isAccountLoading, session } = useAuth()
  const [activeContext, setActiveContextState] = useState<ActiveContext | null>(() =>
    getActiveContext(),
  )
  const [availableContexts, setAvailableContexts] = useState<ActiveContext[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const authPending = authLoading || (!!session && isAccountLoading)

  const applyContextSelection = useCallback(
    (contexts: ActiveContext[], options?: { skipPersisted?: boolean }) => {
      const persisted = options?.skipPersisted ? null : getActiveContext()
      const selected = pickActiveContext(contexts, {
        role: accountData?.role ?? null,
        accountType: accountData?.account_type ?? null,
        persisted,
      })

      if (isAuthDebugEnabled()) {
        console.log('[ActiveContext] Selected context:', selected.scope, selected.scopeId)
      }

      setActiveContextState(selected)
      setActiveContext(selected)
      return selected
    },
    [accountData?.role, accountData?.account_type],
  )

  const switchContext = useCallback((context: ActiveContext) => {
    setActiveContextState(context)
    setActiveContext(context)
  }, [])

  // Load available contexts for the user
  const loadAvailableContexts = useCallback(
    async (options?: { bypassCache?: boolean }) => {
      if (authPending) {
        setIsLoading(true)
        return
      }

      if (!session || !accountId || !accountData) {
        log('[ActiveContext] No session/account, clearing contexts')
        setAvailableContexts([])
        setActiveContextState(null)
        setIsLoading(false)
        return
      }

      if (!options?.bypassCache) {
        const cached = loadContextsFromCache(accountId)
        if (cached && cached.length > 0) {
          log('[ActiveContext] Using cached contexts')
          setAvailableContexts(cached)
          applyContextSelection(cached)
          setIsLoading(false)
          return
        }
      }

      log('[ActiveContext] Loading available contexts for account:', accountId)
      setIsLoading(true)

      try {
        const contexts: ActiveContext[] = []

        // 1. Personal context (always available)
        contexts.push(createPersonalContext(accountId, accountData.first_name || 'You'))

        // 2. Organization contexts (active membership — plan_id optional for lead orgs)
        const { data: orgMemberships, error: orgError } = await supabase
          .from('partner_organization_members')
          .select(
            `
          role,
          status,
          organization_id,
          partner_organizations!inner (
            id,
            organization_name,
            plan_id,
            partnership_stage
          )
        `,
          )
          .eq('account_id', accountId)
          .eq('status', 'active')

        if (orgError) {
          console.error('[ActiveContext] Error loading org contexts:', orgError)
        } else if (orgMemberships && orgMemberships.length > 0) {
          for (const membership of orgMemberships as unknown as OrgMembershipRow[]) {
            const org = membership.partner_organizations
            if (!org?.id) continue
            contexts.push(
              createOrganizationContext(
                org.id,
                org.organization_name,
                membership.role === 'owner',
                membership.role === 'owner' || membership.role === 'admin',
              ),
            )
          }
        }

        if (isAuthDebugEnabled()) console.log('[ActiveContext] Found contexts:', contexts.length)
        setAvailableContexts(contexts)
        saveContextsToCache(accountId, contexts)
        applyContextSelection(contexts)
        setIsLoading(false)
      } catch (error) {
        console.error('[ActiveContext] Error loading contexts:', error)
        setIsLoading(false)
      }
    },
    [accountId, accountData, authPending, session, applyContextSelection],
  )

  // Load contexts when auth changes
  useEffect(() => {
    loadAvailableContexts()
  }, [loadAvailableContexts])

  // Refresh contexts (useful after joining/leaving org)
  const refreshContexts = useCallback(async () => {
    log('[ActiveContext] Refreshing contexts...')
    clearContextsCache()
    await loadAvailableContexts({ bypassCache: true })
  }, [loadAvailableContexts])

  // Clear context on logout
  useEffect(() => {
    if (!accountId) {
      clearActiveContext()
      clearContextsCache()
    }
  }, [accountId])

  const value: ActiveContextState = {
    activeContext,
    availableContexts,
    isLoading,
    switchContext,
    refreshContexts,
  }

  return <ActiveContextContext.Provider value={value}>{children}</ActiveContextContext.Provider>
}
